import React from "react";
import { ArrowLeft, Clock, FileVideo } from "lucide-react";
import { useAppSelector } from "@/redux/hooks/hooks";
import {
  useGetPreviousAnalysisListQuery,
  useLazyGetPreviousAnalysisDetailsQuery,
} from "@/redux/feature/videoDashboard/VideoDashboardApi";
import { AnalysisResult } from "@/redux/feature/videoDashboard/videoDashboardType";

const PreviousAnalysisList: React.FC<{
  onBack: () => void;
  onSelectAnalysis: (analysis: AnalysisResult) => void;
}> = ({ onBack, onSelectAnalysis }) => {
  const { user } = useAppSelector((state) => state.auth);
  const { data: analyses, isLoading } = useGetPreviousAnalysisListQuery(
    { email: user?.email },
    { skip: !user?.email }
  );

  const [getAnalysisDetails] = useLazyGetPreviousAnalysisDetailsQuery();

  const handleAnalysisClick = async (analysis: any) => {
    try {
      const encodedVideoUrl = encodeURIComponent(analysis.video_url);
      const { data: detailedAnalysis } = await getAnalysisDetails({
        email: user?.email,
        video_url: encodedVideoUrl,
      });

      if (detailedAnalysis) {
        onSelectAnalysis({
          ...analysis,
          result: detailedAnalysis,
        });
      } else {
        console.error("No detailed analysis data returned");
      }
    } catch (error) {
      console.error("Failed to fetch analysis details:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
        <p className="text-gray-600 text-base">Loading previous analyses...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
          Previous Analyses
        </h2>
      </div>

      <div className="space-y-3 max-h-[28rem] overflow-y-auto pr-1">
        {analyses?.length === 0 ? (
          <div className="text-center py-12">
            <FileVideo className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-base">
              No previous analyses found
            </p>
          </div>
        ) : (
          analyses?.map((analysis: any) => (
            <div
              key={analysis.video_url}
              onClick={() => handleAnalysisClick(analysis)}
              className="border p-4 rounded-lg hover:bg-gray-50 cursor-pointer transition"
            >
              <div className="flex items-center gap-4">
                <div className="w-16 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <FileVideo className="w-6 h-6 text-gray-400" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm sm:text-base font-medium text-gray-800">
                    {analysis.title || "Video Analysis"}
                  </h4>
                  <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>{analysis.date || "Unknown date"}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PreviousAnalysisList;
